import { useSnackbar } from "notistack";
import { useEffect, useMemo, useRef, useState } from "react";
import { SocialCommentsWrapper } from "./comment/SocialCommentsWrapper";
import { SocialPostWrapper } from "./post/SocialPostWrapper";
import { ReactComponent as Photo } from "assets/img/social/photo.svg";
import { ReactComponent as Poll } from "assets/img/social/poll.svg";
import { ReactComponent as Video } from "assets/img/social/video.svg";
import { Avatar, Spinner } from "components/elements";
import { useCurrentUser } from "hooks/useCurrentUser";
import { trpc } from "lib/trpc";
import { ClientPost, PostCreate } from "@compass/shared/types";
import { SocialPostEditor } from "./SocialPostEditor";
import { PostModal } from "./post/PostModal";

export const SocialPostWithComments = ({
    post,
    limit,
    setActivePost,
}: {
    post: ClientPost;
    limit?: number;
    setActivePost: (post: ClientPost) => void;
}) => {
    const { data: thread } = trpc.social.posts.getThread.useQuery({ postId: post.id });

    return (
        <>
            <SocialPostWrapper post={post} />
            {thread && <SocialCommentsWrapper post={post} threadId={thread.id} limit={limit} />}
            <div onClick={() => setActivePost(post)}>
                <EditPostTrigger isComment />
            </div>
        </>
    );
};

export const EditPostTrigger = ({ isComment }: { isComment?: boolean }) => {
    const currentUser = useCurrentUser();

    return (
        <div
            className={`flex flex-col gap-4 shadow-none ${!isComment ? "rounded-2xl border border-[#d4d5d5] p-4" : ""} `}
        >
            <div className="flex items-center gap-2">
                <Avatar
                    className={`${
                        isComment ? "h-[50px] w-[50px]" : "h-[50px] w-[50px]"
                    } shrink-0 bg-slate-200 text-xs text-slate-400`}
                    photoUrl={currentUser?.avatarUrl}
                    name={currentUser?.name}
                />
                <div className="text-md w-full cursor-pointer rounded-xl border border-[#d4d5d5] bg-[#eeeeee] px-3 py-3 font-normal leading-none text-[#424242]/70">
                    {isComment ? "Add a comment..." : "Join the conversation in the community..."}
                </div>
            </div>
            {!isComment && (
                <div className="flex items-center justify-start gap-6">
                    <div className="h-6 w-6"></div>
                    <Photo className="pointer" />
                    <Video className="pointer" />
                    <Poll className="pointer" />
                </div>
            )}
        </div>
    );
};

export const SocialMain = () => {
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = trpc.useContext();
    const [activePost, setActivePost] = useState<ClientPost | null>(null);
    const { data, isFetching, hasNextPage, isInitialLoading, fetchNextPage } =
        trpc.social.posts.latest.useInfiniteQuery(
            { limit: 4 },
            {
                placeholderData: (prev) => prev,
                getNextPageParam: (lastPage) => {
                    return lastPage.nextCursor;
                },
            },
        );

    const footerRef = useRef(null);

    useEffect(() => {
        if (!footerRef.current) return;

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting && hasNextPage) {
                    fetchNextPage();
                }
            });
        });

        observer.observe(footerRef.current);

        return () => {
            if (!footerRef.current) return;
            observer.unobserve(footerRef.current);
        };
    }, [footerRef, hasNextPage, fetchNextPage]);

    const currentUser = useCurrentUser();
    const [isPostEditOpen, setIsPostEditOpen] = useState<any>();
    const [isCommentEditOpen, setIsCommentEditOpen] = useState<any>();

    const createPostMutation = trpc.social.posts.create.useMutation();

    const handleCreatePost = (params: PostCreate, callback: () => void) => {
        if (createPostMutation.isPending) return;

        createPostMutation.mutate(params, {
            onSuccess: () => {
                queryClient.social.posts.latest.invalidate();
                enqueueSnackbar("Post created successfully.", { variant: "success" });
                callback();
            },
            onError: (e) => {
                console.error(e);
                const zodMessage =
                    (e?.data?.zodError?.flat as any)?.properties?.content?.errors?.[0] ??
                    e?.data?.zodError?.flat?.errors?.[0];

                if (zodMessage) {
                    enqueueSnackbar(zodMessage, { variant: "error" });
                } else {
                    enqueueSnackbar(`${e}`, { variant: "error" });
                }
            },
        });
    };

    const combinedPosts = useMemo(() => {
        if (!data?.pages || data.pages.length === 0) return [];

        return data.pages.flatMap((x) => x.posts as ClientPost[]);
    }, [data]);

    if (isInitialLoading) {
        return (
            <div>
                <Spinner className="text-slate-500" />
            </div>
        );
    }

    return (
        <>
            <SocialPostEditor
                isLoading={createPostMutation.isPending}
                handleEditPost={(params: PostCreate, callback: () => void) =>
                    handleCreatePost(params, callback)
                }
                isOpen={isPostEditOpen}
                setIsOpen={() => setIsPostEditOpen(isPostEditOpen ? undefined : "new-post")}
                currentUser={currentUser}
            >
                <div>
                    <EditPostTrigger />
                </div>
            </SocialPostEditor>
            {combinedPosts?.map((post) => (
                <div
                    key={post.id}
                    className="group relative rounded-2xl border border-[#d4d5d5] p-4 shadow-none"
                    id={`post-${post.id}`}
                >
                    <SocialPostWithComments post={post} limit={2} setActivePost={setActivePost} />
                </div>
            ))}

            {activePost && (
                <PostModal
                    isOpen={Boolean(activePost)}
                    onClose={() => setActivePost(null)}
                    post={activePost}
                />
            )}
            {hasNextPage && (
                <div ref={footerRef} className="flex items-center justify-center pt-8">
                    {isFetching && <Spinner className="h-7 w-7 text-slate-500" />}
                </div>
            )}
        </>
    );
};
